// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

export const NotificationsView = ({ notifications = [], onMarkRead = undefined, onActivate = undefined }: { notifications?: any[]; onMarkRead?: any; onActivate?: (n: any) => void }) => {
  const handleClick = (n: any) => {
    if (onActivate) onActivate(n);
    else if (onMarkRead) onMarkRead(n.id);
  };
  // Compact one-line context for the notifications list. Mirrors the in-email
  // context block but flattened to fit a 12px row. Kept permissive: any
  // combination of fields may be missing on legacy rows.
  const compactContext = (ctx: any): string | null => {
    if (!ctx || typeof ctx !== 'object') return null;
    const parts = [ctx.counterparty, ctx.agreementType, ctx.status].filter((s: any) => s && String(s).trim());
    return parts.length ? parts.join(' · ') : null;
  };
  return (
    <div>
      <SectionTitle sub={`${notifications.filter((n: any) => !n.read).length} unread`} right={<Btn v="ghost" sz="sm" onClick={() => onMarkRead?.("all")}>Mark All Read</Btn>}>Notifications</SectionTitle>
      {notifications.map((n: any) => {
        const ctxLine = compactContext(n.context);
        return (
          <Card key={n.id} onClick={() => handleClick(n)} style={{ marginBottom: 4, padding: 12, cursor: "pointer", borderLeft: `3px solid ${n.read ? "transparent" : T.acc}`, opacity: n.read ? .6 : 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: T.tx }}>{n.msg}</div>
                {ctxLine && <div style={{ fontSize: 11, color: T.txM, marginTop: 2 }}>{ctxLine}</div>}
              </div>
              <span style={{ fontSize: 10, color: T.txD, whiteSpace: "nowrap", marginLeft: 12 }}>{fmtDate(n.date)}</span>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

/* ═══════════════════ SALESFORCE ═══════════════════ */
